/**
 * NavItem Component
 * Individual navigation item for the sidebar.
 * Supports active state highlighting, icons, and click handlers.
 *
 * Ported from v0.26 legacy app — adapted for verisa_v1 project structure.
 */

import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { colors } from '../../theme/colors';

export interface NavItemData {
  /** Display text for the navigation item */
  text: string;
  /** Icon element to display (MUI icon or SVG) */
  icon: React.ReactNode;
  /** Route path to navigate to */
  path: string;
  /** Optional: Additional paths that should highlight this item */
  matchPaths?: string[];
}

interface NavItemProps {
  /** Navigation item data */
  item: NavItemData;
  /** Whether the sidebar is collapsed (icon-only mode) */
  collapsed?: boolean;
  /** Override for selected state */
  isSelected?: boolean;
}

/**
 * Determines if a nav item should be highlighted as active
 * based on the current route.
 */
const isPathActive = (
  currentPath: string,
  itemPath: string,
  matchPaths?: string[],
): boolean => {
  // Exact match
  if (currentPath === itemPath) return true;

  // Check if current path starts with item path (for nested routes)
  if (currentPath.startsWith(itemPath) && itemPath !== '/') return true;

  // Check additional match paths
  if (matchPaths) {
    return matchPaths.some(
      (mp) => currentPath === mp || currentPath.startsWith(mp),
    );
  }

  return false;
};

/**
 * NavItem renders a single navigation item in the sidebar.
 * Features:
 * - Active state highlighting with visual indicator
 * - Icon + text layout (or icon-only when collapsed)
 * - Tooltip support in collapsed mode
 * - Keyboard accessible (Enter / Space)
 */
const NavItem: React.FC<NavItemProps> = ({
  item,
  collapsed = false,
  isSelected: isSelectedOverride,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const isSelected =
    isSelectedOverride !== undefined
      ? isSelectedOverride
      : isPathActive(location.pathname, item.path, item.matchPaths);

  const handleClick = () => {
    navigate(item.path);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      navigate(item.path);
    }
  };

  const content = (
    <Box
      component="button"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={item.text}
      aria-current={isSelected ? 'page' : undefined}
      sx={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        border: 'none',
        background: 'none',
        borderRadius: 1.5,
        px: collapsed ? 1.5 : 2.5,
        py: 1.5,
        fontWeight: isSelected ? 600 : 500,
        fontSize: '14px',
        transition: 'all 0.2s ease',
        gap: collapsed ? 0 : 1,
        color: isSelected ? colors.text.selected : colors.text.secondary,
        cursor: 'pointer',
        textDecoration: 'none',
        justifyContent: collapsed ? 'center' : 'flex-start',
        '&:hover': {
          bgcolor: 'action.hover',
          color: colors.interactive.hover,
        },
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: 2,
        },
      }}
    >
      {/* Background highlight for selected item */}
      {isSelected && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.05)',
          }}
        />
      )}

      {/* Icon */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          height: 20,
          width: 20,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          lineHeight: 1,
          '& svg': {
            width: '100%',
            height: '100%',
          },
        }}
      >
        {item.icon}
      </Box>

      {/* Text - hidden in collapsed mode */}
      {!collapsed && (
        <Typography
          component="span"
          sx={{
            position: 'relative',
            zIndex: 10,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'left',
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 1.2,
            color: 'inherit',
          }}
        >
          {item.text}
        </Typography>
      )}
    </Box>
  );

  // Wrap with tooltip only in collapsed mode
  if (collapsed) {
    return (
      <Tooltip title={item.text} placement="right" arrow>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export default NavItem;
